/**
 * 阿里云验证码 2.0 — Web/H5 popup 集成
 * @see https://help.aliyun.com/zh/captcha/captcha2-0/user-guide/new-architecture-for-web-and-h5-client-access
 *
 * 策略：页面加载后预初始化实例；用户点击时同步 show()，避免异步链丢失手势/初始化记录。
 */

const ALIYUN_CAPTCHA_SCRIPT = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js'

export type CaptchaScene = 'login' | 'register' | 'sms_send'

export const CAPTCHA_CLOSED_ERROR = 'captcha closed'

interface CaptchaInstance {
  show?: () => void
  hide?: () => void
  destroyCaptcha?: () => void
  startTracelessVerification?: () => void
}

declare global {
  interface Window {
    AliyunCaptchaConfig?: { region: string; prefix: string }
    initAliyunCaptcha?: (opts: Record<string, unknown>) => void
  }
}

let scriptPromise: Promise<void> | null = null
let captchaInstance: CaptchaInstance | null = null
let activeInitKey: string | null = null
let initPromise: Promise<void> | null = null
let initChain: Promise<void> = Promise.resolve()

type PendingHandlers = {
  resolve: (param: string) => void
  reject: (err: Error) => void
}

let pending: PendingHandlers | null = null

function loadAliyunCaptchaScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
  if (window.initAliyunCaptcha) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${ALIYUN_CAPTCHA_SCRIPT}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('captcha script load failed')))
      if (window.initAliyunCaptcha) resolve()
      return
    }
    const script = document.createElement('script')
    script.src = ALIYUN_CAPTCHA_SCRIPT
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('captcha script load failed'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

function configKey(opts: RunAliyunCaptchaOptions): string {
  return `${opts.prefix}|${opts.region}|${opts.sceneId}|${opts.language || 'cn'}`
}

function normalizeVerifyParam(raw: unknown): string {
  if (typeof raw === 'string') return raw.trim()
  if (raw != null && typeof raw === 'object') {
    try {
      return JSON.stringify(raw)
    } catch {
      return ''
    }
  }
  return ''
}

function teardownCaptchaHost() {
  try {
    captchaInstance?.destroyCaptcha?.()
  } catch {
    // ignore
  }
  captchaInstance = null
  initPromise = null
  activeInitKey = null
  document.getElementById('aliyun-captcha-host')?.remove()
}

function rejectPending(err: Error) {
  pending?.reject(err)
  pending = null
}

function ensureCaptchaInitialized(opts: RunAliyunCaptchaOptions): Promise<void> {
  const key = configKey(opts)
  if (initPromise && activeInitKey === key && captchaInstance) {
    return initPromise
  }

  initChain = initChain
    .catch(() => {
      // 前序 init 失败不阻塞后续重试
    })
    .then(() => startCaptchaInitialization(opts, key))

  initPromise = initChain
  return initPromise
}

function startCaptchaInitialization(opts: RunAliyunCaptchaOptions, key: string): Promise<void> {
  teardownCaptchaHost()
  activeInitKey = key

  return new Promise((resolveInit, rejectInit) => {
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const elementId = `aliyun-captcha-element-${uid}`
    const buttonId = `aliyun-captcha-button-${uid}`

    window.AliyunCaptchaConfig = {
      region: opts.region || 'cn',
      prefix: opts.prefix,
    }

    const host = document.createElement('div')
    host.id = 'aliyun-captcha-host'
    host.style.cssText =
      'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;'
    host.innerHTML = `<div id="${elementId}"></div><button type="button" id="${buttonId}"></button>`
    document.body.appendChild(host)

    void loadAliyunCaptchaScript()
      .then(() => {
        if (!window.initAliyunCaptcha) {
          teardownCaptchaHost()
          rejectInit(new Error('initAliyunCaptcha missing'))
          return
        }

        window.initAliyunCaptcha({
          SceneId: opts.sceneId,
          mode: 'popup',
          element: `#${elementId}`,
          button: `#${buttonId}`,
          language: opts.language || 'cn',
          slideStyle: { width: 360, height: 40 },
          delayBeforeSuccess: false,
          getInstance: (instance: CaptchaInstance) => {
            captchaInstance = instance
            resolveInit()
          },
          success: (captchaVerifyParam: unknown) => {
            const param = normalizeVerifyParam(captchaVerifyParam)
            if (!param) {
              rejectPending(new Error('captcha empty param'))
              teardownCaptchaHost()
              return
            }
            pending?.resolve(param)
            pending = null
            teardownCaptchaHost()
          },
          fail: () => {
            // 有效期内 SDK 自动刷新
          },
          onClose: () => {
            rejectPending(new Error(CAPTCHA_CLOSED_ERROR))
            teardownCaptchaHost()
          },
          onError: () => {
            rejectPending(new Error('captcha init error'))
            teardownCaptchaHost()
          },
        })
      })
      .catch((err) => {
        teardownCaptchaHost()
        rejectInit(err instanceof Error ? err : new Error('captcha script load failed'))
      })
  })
}

export interface RunAliyunCaptchaOptions {
  prefix: string
  region: string
  sceneId: string
  language?: string
}

/** 登录页加载后预初始化,减少点击时异步等待 */
export function prewarmAliyunCaptcha(opts: RunAliyunCaptchaOptions): void {
  if (typeof document === 'undefined') return
  void ensureCaptchaInitialized(opts).catch(() => {
    // 预加载失败时,点击获取验证码会重试
  })
}

export function runAliyunCaptcha(opts: RunAliyunCaptchaOptions): Promise<string> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('captcha unavailable'))
  }
  if (pending) {
    return Promise.reject(new Error('captcha in progress'))
  }

  return new Promise((resolve, reject) => {
    pending = { resolve, reject }

    void ensureCaptchaInitialized(opts)
      .then(() => {
        if (!captchaInstance?.show) {
          rejectPending(new Error('captcha trigger missing'))
          return
        }
        // 在用户点击栈内同步弹出(调用方须在 click handler 中 await)
        captchaInstance.show()
        captchaInstance.startTracelessVerification?.()
      })
      .catch((err) => {
        rejectPending(err instanceof Error ? err : new Error('captcha failed'))
      })
  })
}
